import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { faBan, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import qs from 'qs';

import Button, { ButtonSizeEnum } from 'components/stateless/Button/Button';
import { FormBorder } from 'components/stateless/CommonForm/FormBorder';
import { FormContents } from 'components/stateless/CommonForm/FormContents';
import { FormErrorMessage } from 'components/stateless/CommonForm/FormErrorMessage';
import { FormValue } from 'components/stateless/CommonForm/FormValue';
import GuideMessage from 'components/stateless/GuideMessage/GuideMessage';
import PublicHeaderBar from 'components/stateless/HeaderBar/PublicHeaderBar';
import MSWDevTool from 'components/stateless/MSWDevTool';
import PasswordValidationBox from 'components/stateless/PasswordValidationBox/PasswordValidationBox';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { ROUTES_CM } from 'constants/routes/common';
import { ENTERPRISE_TYPE } from 'enums';
import useMounted from 'hooks/useMounted';
import type { EnterpriseVOModel } from 'models/vo/EnterpriseVO';
import type { TermsOfUseVersionVOModel } from 'models/vo/TermsOfUseVersionVO';
import type { UserVOModel } from 'models/vo/UserVO';
import { formErrorHandler } from 'utils/error/manager';
import API_AN from 'utils/http/api/anonymous';
import { requestInvitedEnterprise } from 'utils/http/api/anonymous/enterprises';
import { requestTermsOfUseVersions } from 'utils/http/api/anonymous/terms-of-use-versions';
import {
  requestCheckUserExistence,
  requestInvitedUser,
  requestSignUpAcHqOpAndDeOp,
} from 'utils/http/api/anonymous/users';
import type { SignUpDealerOperatorRequest } from 'utils/http/api/anonymous/users/requests';
import useModal from 'utils/modal/useModal';
import { REG_LOWERCASE_AND_NUMBER } from 'utils/validation/regExp';

function AnchorHqOperatorAndDealerOperatorSignUp() {
  const query = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });
  const emailCode = query.code as string;

  const { t } = useTranslation(['format']);
  const modal = useModal();
  const mounted = useMounted();
  const history = useHistory();

  const [dealerUser, setDealerUser] = useState<UserVOModel>();
  const [enterprise, setEnterprise] = useState<EnterpriseVOModel>();
  const [isCheckID, setCheckID] = useState<boolean | undefined>(undefined);
  const [loginIdErrorMessage, setLoginIdErrorMessage] = useState<string>('');
  const [checkedTOUs, setCheckedTOUs] = useState<number[]>([]);
  const [termsOfUseVersions, setTermsOfUseVersions] = useState<TermsOfUseVersionVOModel[]>();

  // Request UseForm
  const { register, getValues, setError, clearErrors, watch, errors } = useForm<SignUpDealerOperatorRequest>();

  const {
    setError: setCheckIdError,
    clearErrors: clearCheckIdErrors,
    errors: checkIdErrors,
  } = useForm<{ loginId: string }>();

  const isLoginIdError = errors.loginId || checkIdErrors.loginId;

  const { newPassword, confirmPassword } = watch();

  // onMounted
  useEffect(() => {
    if (mounted) {
      fetchAll();
    }
  }, [mounted]);

  // Methods
  const fetchAll = async () => {
    try {
      const [fetchedUser, fetchedEnterprise, fetchedTermsOfUseVersions] = await Promise.all([
        requestInvitedUser(emailCode),
        requestInvitedEnterprise(emailCode),
        requestTermsOfUseVersions(emailCode),
      ]);

      ReactDOM.unstable_batchedUpdates(() => {
        setDealerUser(fetchedUser);
        setEnterprise(fetchedEnterprise);
        setTermsOfUseVersions(fetchedTermsOfUseVersions);
      });
    } catch (e) {
      modal.show(e);
    }
  };

  const checkId = async () => {
    setCheckID(undefined);
    try {
      const response = await requestCheckUserExistence({ loginId: getValues().loginId, code: emailCode });

      if (!response) {
        modal.show(
          <h6>
            {t('text:This_ID_is_available_for_use')}
            <br />
            {t('text:Please_continue_your_sign_up_process')}
          </h6>,
        );
      } else {
        modal.show(
          <h6>
            {t('text:This_ID_is_already_in_use')}
            <br />
            {t('text:Please_type_in_another_ID_to_use')}
          </h6>,
        );
      }

      setCheckID(!response);
    } catch (error) {
      modal.show(error);
      if (!getValues('loginId')) setCheckID(false);
      formErrorHandler<{ loginId: string }>(error, setCheckIdError, clearCheckIdErrors);
    }
  };

  const submitRegister = async () => {
    const data = getValues();

    if (!data.loginId || !isCheckID) setCheckID(false);
    if (data.loginId && !isCheckID) return;

    try {
      data.code = emailCode;
      const userTouList: any[] = [];

      termsOfUseVersions?.forEach(termsOfUseVersion => {
        userTouList.push({
          touVersionId: termsOfUseVersion.termsOfUseVersionId,
          financierId: termsOfUseVersion.relatedFinancierId,
        });
      });

      await requestSignUpAcHqOpAndDeOp({ ...data, userTouList });
      history.push(ROUTES_CM.SIGN_UP_COMPLETE, {
        userId: data.loginId,
        userName: dealerUser?.userName,
        userAuthority: dealerUser?.authorityType,
      });
    } catch (error) {
      modal.show(error);

      formErrorHandler<SignUpDealerOperatorRequest>(error, setError, clearErrors);
    }
  };

  const checkSamePassword = (newPassword: string, confirmPassword: string) => {
    return newPassword === confirmPassword;
  };

  const checkIdValidation = () => {
    if (REG_LOWERCASE_AND_NUMBER.test(getValues('loginId'))) {
      setLoginIdErrorMessage('');
    } else {
      setLoginIdErrorMessage(t('text:ID_can_only_be_used_in_lowercase_letters_and_numbers'));
    }
  };

  const handleTOUCheckBox = (e: any, termsOfUseVersionId: number) => {
    if (e.target.checked) {
      setCheckedTOUs([...checkedTOUs, termsOfUseVersionId]);
    } else {
      setCheckedTOUs(checkedTOUs.filter(item => item !== termsOfUseVersionId));
    }
  };

  return (
    <>
      <PublicHeaderBar />
      <div className="common-content pb-5" data-testid="signUpContent">
        <h1 className="signup-title">{t('text:Sign_up')}</h1>
        <GuideMessage
          message={[
            t(
              'text:After_reviewing_the_user_and_company_information_enter_the_account_information_to_complete_the_sign_up_process',
            ),
            t('text:Please_contact_the_responsible_financier_branch_if_the_information_below_is_incorrect'),
          ]}
          isImportContentArea
        />
        <SectionTitle title={t('text:User_Information')} />
        <FormBorder>
          <FormContents>
            <div className="row">
              <FormValue label={t('text:Name')} className="information-form__input" value={dealerUser?.userName} />
              <FormValue label={t('text:Mobile')} className="information-form__input" value={dealerUser?.mobile} />
            </div>
            <div className="row">
              <FormValue label={t('text:Email')} className="information-form__input" value={dealerUser?.email} />
              <FormValue
                label={t('text:Authority')}
                className="information-form__input"
                value={t(`code:authority-type.${dealerUser?.authorityType}`)}
              />
            </div>
          </FormContents>
        </FormBorder>
        <SectionTitle title={t('text:Company_Information')} className="mt-5" />
        <FormBorder>
          <FormContents>
            <div className="row">
              <FormValue
                label={t('text:Name')}
                className="information-form__input"
                value={enterprise?.enterpriseName}
              />
              <FormValue label={t('text:Tax_Code')} className="information-form__input" value={enterprise?.taxCode} />
            </div>
            <div className="row">
              <FormValue
                label={t('text:Company_Registration_Number')}
                className="information-form__input"
                value={enterprise?.businessCode}
              />
              <FormValue
                col={3}
                label={t('text:Telephone')}
                className="information-form__input"
                value={enterprise?.telephone}
              />
              <FormValue col={3} label={t('text:Fax')} className="information-form__input" value={enterprise?.fax} />
            </div>
            <div className="row">
              <FormValue
                label={t('text:Legal_Representative_Name')}
                className="information-form__input"
                value={enterprise?.representativeName}
              />
              <FormValue
                col={3}
                label={t('text:Legal_Representative_Title')}
                className="information-form__input"
                value={enterprise?.representativePosition}
              />
              <FormValue
                col={3}
                label={t('text:Legal_Representative_Email')}
                className="information-form__input"
                value={enterprise?.representativeEmail}
              />
            </div>
            <div className="row">
              <FormValue
                col={12}
                label={t('text:Registered_Office_Address')}
                className="information-form__input"
                value={enterprise?.address}
              />
            </div>
          </FormContents>
        </FormBorder>
        <SectionTitle title={t('text:Account_Information')} className="mt-5" />

        <form>
          <FormBorder editable>
            <FormContents>
              <div className="row">
                <div className="col-6">
                  <label id="loginId" className="information-form__label star">
                    {t('text:ID')}
                  </label>
                  <div className="d-flex">
                    <input
                      aria-labelledby="loginId"
                      className={`information-form__input ${
                        isLoginIdError || isCheckID === false ? 'error-input-border' : ''
                      }`}
                      type="text"
                      name="loginId"
                      ref={register}
                      autoComplete="off"
                      onChange={() => {
                        checkIdValidation();
                        setCheckID(undefined);
                        clearCheckIdErrors('loginId');
                        clearErrors('loginId');
                      }}
                    />
                    <Button onClick={checkId} disabled={loginIdErrorMessage !== ''}>
                      {t('text:Check_ID')}
                    </Button>
                  </div>
                  {isCheckID && (
                    <div className="text-green mt-1">
                      <FontAwesomeIcon icon={faCheck} /> {t('text:Complete')}
                    </div>
                  )}
                  {isCheckID === false ? (
                    <div className="text-brick-red m-1">
                      {t('text:Please_click_on_the_Check_ID_button_to_see_whether_the_ID_is_available_for_use')}
                    </div>
                  ) : loginIdErrorMessage !== '' ? (
                    <div className="text-brick-red m-1">{loginIdErrorMessage}</div>
                  ) : isLoginIdError ? (
                    <FormErrorMessage error={isLoginIdError} />
                  ) : null}
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <label id="newPassword" className="information-form__label star">
                    {t('text:Password')}
                  </label>
                  <input
                    aria-labelledby="newPassword"
                    className={`information-form__input ${errors.newPassword ? 'error-input-border' : ''}`}
                    type="password"
                    name="newPassword"
                    ref={register}
                    autoComplete="new-password"
                    onChange={() => clearErrors('newPassword')}
                  />
                  <FormErrorMessage error={errors.newPassword} />
                </div>
                <div className="col-6 position-parent">
                  <div style={{ position: 'absolute', top: '15px', width: '350px' }}>
                    <PasswordValidationBox newPassword={newPassword} />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <label id="confirmPassword" className="information-form__label star">
                    {t('text:Confirm_Password')}
                  </label>
                  <input
                    aria-labelledby="confirmPassword"
                    className={`information-form__input ${errors.confirmPassword ? 'error-input-border' : ''}`}
                    type="password"
                    name="confirmPassword"
                    ref={register}
                    autoComplete="new-password"
                    onChange={() => clearErrors('confirmPassword')}
                  />
                  {!checkSamePassword(newPassword, confirmPassword) ? (
                    <div className="text-brick-red mt-1">
                      <FontAwesomeIcon icon={faBan} /> {t('text:Password_do_not_match')}
                    </div>
                  ) : errors.confirmPassword ? (
                    <FormErrorMessage error={errors.confirmPassword} />
                  ) : null}
                </div>
              </div>
            </FormContents>
          </FormBorder>
          {termsOfUseVersions?.map(termsOfUseVersion => (
            <div key={termsOfUseVersion.termsOfUseVersionId} className="TOU-check-wrap">
              <input
                id={String(termsOfUseVersion.termsOfUseVersionId)}
                className="form-check-input"
                type="checkbox"
                checked={checkedTOUs.includes(termsOfUseVersion.termsOfUseVersionId)}
                onChange={e => handleTOUCheckBox(e, termsOfUseVersion.termsOfUseVersionId)}
              />
              <Trans
                i18nKey={
                  termsOfUseVersion.enterpriseType === ENTERPRISE_TYPE.SY
                    ? 'text:I_have_read_and_agree_to_the_Fin2B_Supply_Chain_Financing_Platform_s_Terms_and_Conditions_of_Use'
                    : 'text:I_have_read_and_agree_to_financierName_s_Terms_and_Conditions_of_Use'
                }
                components={{
                  1: <label className="me-1" htmlFor={String(termsOfUseVersion.termsOfUseVersionId)} />,
                  2: (
                    <a
                      className="link-text-color"
                      href={`${process.env.REACT_APP_BASE_URL}${API_AN.TERMS_OF_USE_VERSIONS.DOWNLOAD(
                        termsOfUseVersion.termsOfUseVersionId,
                      )}?type=INLINE`}
                      target={`tou-${termsOfUseVersion.termsOfUseVersionId}`}
                    />
                  ),
                }}
                values={
                  termsOfUseVersion.enterpriseType === ENTERPRISE_TYPE.SY
                    ? undefined
                    : { financierName: termsOfUseVersion.relatedFinancierName }
                }
              />
            </div>
          ))}
          <div className="flex-end mt-5">
            <Button
              size={ButtonSizeEnum.LG}
              disabled={checkedTOUs.length !== termsOfUseVersions?.length}
              onClick={submitRegister}
            >
              {t('text:Register')}
            </Button>
          </div>
        </form>
      </div>
      <MSWDevTool />
    </>
  );
}

export default AnchorHqOperatorAndDealerOperatorSignUp;
